import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, FlaskConical, Inbox, ClipboardCheck, ShieldCheck, AlertTriangle, Archive, Plus, ChevronLeft, ChevronRight, AlertCircle, Loader2 } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CreateBookingModal } from "@/components/CreateBookingModal";
import { ArchiveActionsMenu } from "@/components/ArchiveActionsMenu";
import { ArchiveStatusFilter, useArchiveFilter } from "@/components/ArchiveStatusFilter";
import { isBookingResolvedForPayment } from "@/lib/bookingPaymentStatus";
import { useAuth } from "@/lib/auth";
import api, { formatApiError } from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";
import { toast } from "sonner";

const STATUS_META = {
  pending_sample:  { label: "Pending sample",   bg: "bg-slate-100",   text: "text-slate-700",   icon: Inbox },
  received:        { label: "Received",         bg: "bg-blue-50",     text: "text-blue-700",    icon: FlaskConical },
  results_entered: { label: "Results entered",  bg: "bg-amber-50",    text: "text-amber-700",   icon: ClipboardCheck },
  awaiting_review: { label: "Awaiting review",  bg: "bg-orange-50",   text: "text-orange-700",  icon: AlertTriangle },
  approved:        { label: "Approved",         bg: "bg-emerald-50",  text: "text-emerald-700", icon: ShieldCheck },
  published:       { label: "Published",        bg: "bg-purple-50",   text: "text-purple-700",  icon: ShieldCheck },
  draft:           { label: "Draft",            bg: "bg-slate-100",   text: "text-slate-600",   icon: Inbox },
  cancelled:       { label: "Cancelled",        bg: "bg-rose-50",     text: "text-rose-700",    icon: Archive },
};

const FILTER_STATUSES = ["all", "pending_sample", "received", "results_entered", "awaiting_review", "approved", "published"];
const PAGE_SIZES = [25, 50, 100];

export default function AdminLab() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [demoHidden, setDemoHidden] = useState(0);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  // Jun 2026 — the Monthly Performance dashboard drills through to
  // /admin/lab?status=<workflow status>, so honour it on first load.
  const [status, setStatus] = useState(
    () => new URLSearchParams(window.location.search).get("status") || "all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [includeUnpaid, setIncludeUnpaid] = useState(false);
  const [hideDemo, setHideDemo] = useState(true);
  const [archived, setArchived] = useArchiveFilter("admin.lab.archived");
  const [showCreate, setShowCreate] = useState(false);

  const isAdmin = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.set("q", query);
      if (status !== "all") params.set("status_filter", status);
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      if (includeUnpaid) params.set("include_unpaid", "true");
      if (!hideDemo) params.set("hide_demo", "false");
      if (archived !== "active") params.set("archived", archived);
      const { data } = await api.get(`/lab/tests?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setDemoHidden(data.demo_hidden || 0);
    } finally {
      setLoading(false);
    }
  };

  // Reset to page 1 whenever filters or search change.
  useEffect(() => { setPage(1); }, [query, status, pageSize, includeUnpaid, hideDemo, archived]);

  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, status, page, pageSize, includeUnpaid, hideDemo, archived]);

  const counts = items.reduce((acc, { test }) => {
    acc[test.status] = (acc[test.status] || 0) + 1;
    return acc;
  }, {});

  // Phase 2L-A42 — Admin-only bulk archive on the Lab Queue. Same
  // pattern as `/admin/bookings`: derive everything from
  // `selectedRefs ∩ visible` so filter / status / page changes naturally
  // drop out-of-scope refs. Reuses the existing
  // `PATCH /api/admin/bookings/bulk-archive` endpoint.
  const [selectedRefs, setSelectedRefs] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const visibleRefs = useMemo(
    () => items.map(({ test }) => test.booking_reference).filter(Boolean),
    [items],
  );
  const allVisibleSelected = visibleRefs.length > 0
    && visibleRefs.every((r) => selectedRefs.has(r));
  const someVisibleSelected = !allVisibleSelected
    && visibleRefs.some((r) => selectedRefs.has(r));
  const selectedItems = items.filter(
    ({ test }) => test.booking_reference && selectedRefs.has(test.booking_reference),
  );
  const selectedCount = selectedItems.length;
  const selectedPaidCount = selectedItems.filter(
    ({ payment }) => payment?.is_paid,
  ).length;

  const toggleOne = (ref) => {
    if (!ref) return;
    setSelectedRefs((prev) => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref); else next.add(ref);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelectedRefs((prev) => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        for (const r of visibleRefs) next.delete(r);
        return next;
      }
      const next = new Set(prev);
      for (const r of visibleRefs) next.add(r);
      return next;
    });
  };
  const clearSelection = () => setSelectedRefs(new Set());

  const runBulkArchive = async () => {
    if (selectedCount === 0) return;
    setBulkBusy(true);
    try {
      const refs = selectedItems.map(({ test }) => test.booking_reference);
      const { data } = await api.patch("/admin/bookings/bulk-archive", {
        refs,
        reason: `Bulk archive from Lab Queue (${refs.length} bookings)`,
      });
      const bits = [];
      if (data.total_archived) bits.push(`${data.total_archived} archived`);
      if (data.already_archived?.length) bits.push(`${data.already_archived.length} already archived`);
      if (data.not_found?.length) bits.push(`${data.not_found.length} not found`);
      if (data.paid_archived_count) bits.push(`${data.paid_archived_count} paid`);
      toast.success(`Archive done · ${bits.join(" · ")}`);
      clearSelection();
      setBulkOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBulkBusy(false);
    }
  };

  const fmt = (d) => formatDateAU(d);

  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  // Compute visible page-number buttons (max 5 around current).
  const pageNumbers = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
  } else {
    pageNumbers.push(1);
    const around = [page - 1, page, page + 1].filter((p) => p > 1 && p < totalPages);
    if (around[0] > 2) pageNumbers.push("…");
    pageNumbers.push(...around);
    if (around[around.length - 1] < totalPages - 1) pageNumbers.push("…");
    pageNumbers.push(totalPages);
  }

  return (
    <PortalShell title="Laboratory · Results Entry" subtitle="Lab operations">
      {isAdmin && (
        <div className="flex justify-end mb-3">
          <Button
            data-testid="lab-create-booking"
            onClick={() => setShowCreate(true)}
            className="rounded-full font-semibold"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Create booking
          </Button>
        </div>
      )}

      {/* Filter pills */}
      <div className="flex flex-wrap gap-2 mb-4">
        {FILTER_STATUSES.map((s) => {
          const m = s === "all" ? { label: "All", bg: "bg-slate-100", text: "text-slate-700" } : STATUS_META[s];
          const active = status === s;
          return (
            <button
              key={s}
              onClick={() => setStatus(s)}
              data-testid={`lab-filter-${s}`}
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                active ? "bg-slate-900 text-white" : `${m.bg} ${m.text} hover:opacity-80`
              }`}
            >
              {m.label}
              {s === "all" && total > 0 && (
                <span className={`px-1.5 py-0.5 rounded text-[10px] ${active ? "bg-white/20" : "bg-white/60"}`}>
                  {total}
                </span>
              )}
              {s !== "all" && (counts[s] || 0) > 0 && (
                <span className={`px-1.5 py-0.5 rounded text-[10px] ${active ? "bg-white/20" : "bg-white/60"}`}>
                  {counts[s]}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search + page size + admin toggles */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[260px] max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="lab-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search reference, customer, suburb, address, mobile, email…"
            className="pl-10 h-11 rounded-full bg-white"
          />
        </div>
        <div className="flex items-center gap-2 text-sm">
          <label className="text-slate-600">Per page</label>
          <select
            data-testid="lab-page-size"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm"
          >
            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        {isAdmin && (
          <>
            <label className="inline-flex items-center gap-2 text-xs text-slate-700" data-testid="lab-toggle-include-unpaid">
              <input type="checkbox" checked={includeUnpaid} onChange={(e) => setIncludeUnpaid(e.target.checked)} />
              Show unpaid / abandoned
            </label>
            <label className="inline-flex items-center gap-2 text-xs text-slate-700" data-testid="lab-toggle-hide-demo">
              <input type="checkbox" checked={!hideDemo} onChange={(e) => setHideDemo(!e.target.checked)} />
              Show demo / test bookings
            </label>
            <ArchiveStatusFilter value={archived} onChange={setArchived} testId="lab-archive-filter" />
          </>
        )}
      </div>

      {archived !== "active" && (
        <div data-testid="lab-archived-banner" className="mb-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-2 text-xs text-amber-800 font-medium">
          Showing {archived === "archived" ? "archived" : "all"} lab bookings. Use the row menu (⋯) to restore an archived booking.
        </div>
      )}

      {/* Result count */}
      <div className="flex items-center justify-between mb-2 text-sm text-slate-600">
        <div data-testid="lab-result-count">
          {total === 0 ? "No results" : `Showing ${rangeStart}–${rangeEnd} of ${total}`}
          {demoHidden > 0 && hideDemo && (
            <span className="ml-3 text-xs text-slate-400">({demoHidden} demo {demoHidden === 1 ? "row" : "rows"} hidden)</span>
          )}
        </div>
      </div>

      <div data-testid="lab-tests-table" className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
        {isAdmin && selectedCount > 0 && (
          <div
            data-testid="lab-bulk-action-bar"
            className="flex flex-wrap items-center justify-between gap-3 bg-cyan-50 border-b border-cyan-200 px-4 py-3"
          >
            <div className="text-sm font-semibold text-cyan-900">
              <span data-testid="lab-bulk-selected-count">{selectedCount}</span> selected
              {selectedPaidCount > 0 && (
                <span
                  data-testid="lab-bulk-selected-paid-count"
                  className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px]"
                >
                  <AlertCircle className="w-3 h-3" /> {selectedPaidCount} paid
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                data-testid="lab-bulk-clear-selection"
                variant="ghost"
                size="sm"
                onClick={clearSelection}
                className="h-8"
              >
                Clear selection
              </Button>
              <Button
                data-testid="lab-bulk-archive-button"
                onClick={() => setBulkOpen(true)}
                size="sm"
                className="h-8 bg-slate-900 hover:bg-slate-800 text-white"
              >
                <Archive className="w-4 h-4 mr-1.5" />
                Archive selected
              </Button>
            </div>
          </div>
        )}
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading tests...</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            No tests match the current filter.
            {query && <div className="mt-2 text-xs">Try clearing the search box or status filter.</div>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  {isAdmin && (
                    <th className="text-left p-4 w-10">
                      <Checkbox
                        data-testid="lab-bulk-select-all"
                        checked={allVisibleSelected ? true : (someVisibleSelected ? "indeterminate" : false)}
                        onCheckedChange={toggleAllVisible}
                        aria-label="Select all visible lab tests"
                      />
                    </th>
                  )}
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Booking / Sample</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Customer</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Property</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Test type</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Status</th>
                  <th className="text-right p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map(({ test, property, customer, payment, booking }) => {
                  const meta = STATUS_META[test.status] || STATUS_META.draft;
                  const Icon = meta.icon;
                  const payStatus = payment?.payment_status;
                  // Phase 2L-A33 — `payment_status="not_required"` is a
                  // valid, processable state (admin/lab-created zero-charge).
                  // Treat it like paid for queue UI.
                  // Phase 2L-A62 — use the canonical helper so the Lab
                  // queue's "AWAITING PAYMENT" pill matches the
                  // /admin/bookings Paid badge. Previously the local
                  // logic flagged rows whose `is_paid: 1` legacy
                  // backfill values were truthy-not-True as unpaid.
                  const isUnpaid = payment && !isBookingResolvedForPayment({
                    is_paid: payment.is_paid,
                    payment_status: payStatus,
                  });
                  return (
                    <tr key={test.id} data-testid={`lab-row-${test.id}`} className={`border-t border-slate-100 hover:bg-slate-50/50 ${isUnpaid ? "bg-rose-50/30" : ""}`}>
                      {isAdmin && (
                        <td className="p-4 align-top">
                          <Checkbox
                            data-testid={`lab-bulk-select-${test.booking_reference}`}
                            checked={test.booking_reference ? selectedRefs.has(test.booking_reference) : false}
                            onCheckedChange={() => toggleOne(test.booking_reference)}
                            disabled={!test.booking_reference}
                            aria-label={`Select ${test.booking_reference || "test"}`}
                          />
                        </td>
                      )}
                      <td className="p-4">
                        <div className="font-mono text-xs font-bold text-slate-900">{test.booking_reference || "—"}</div>
                        {test.sample_number && (
                          <div className="text-[11px] text-cyan-700 font-bold mt-0.5">Sample {test.sample_number}</div>
                        )}
                        {test.customer_sample_reference && (
                          <div className="text-[11px] text-slate-700 mt-0.5" data-testid={`lab-sample-ref-${test.id}`}>
                            <span className="font-semibold">Ref:</span> {test.customer_sample_reference}
                          </div>
                        )}
                        {test.sample_point && (
                          <div className="text-[11px] text-slate-700 mt-0.5" data-testid={`lab-sample-point-${test.id}`}>
                            <span className="font-semibold">Point:</span> {test.sample_point}
                          </div>
                        )}
                        <div className="text-[10px] text-slate-400 mt-0.5">{fmt(test.collected_at)}</div>
                        {payStatus === "not_required" && (
                          <div className="mt-1 inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-slate-200 text-slate-700" data-testid={`lab-no-payment-required-badge-${test.id}`}>
                            No payment required
                          </div>
                        )}
                        {isUnpaid && (
                          <div className="mt-1 inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-rose-200 text-rose-800" data-testid={`lab-unpaid-badge-${test.id}`}>
                            {payment?.collected_awaiting_payment ? "Collected — Awaiting Payment · Hold" :
                             payStatus === "abandoned" ? "Abandoned · Do not process" :
                             payStatus === "superseded" ? "Superseded · Do not process" :
                             payStatus === "failed" ? "Payment failed" :
                             "Awaiting payment"}
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="font-medium text-slate-900">{customer?.name || "—"}</div>
                        <div className="text-xs text-slate-500">{customer?.email || ""}</div>
                      </td>
                      <td className="p-4 text-xs text-slate-600 max-w-[220px] truncate" title={property?.address}>
                        {property?.address || "—"}
                        {property?.suburb && <div className="text-[10px] text-slate-400">{property.suburb}</div>}
                      </td>
                      <td className="p-4 text-slate-700">{test.test_name}</td>
                      <td className="p-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.bg} ${meta.text}`}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <div className="inline-flex items-center gap-2 justify-end">
                          <Link
                            to={`/admin/lab/${test.id}`}
                            data-testid={`lab-open-${test.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-cyan-600 transition-colors"
                          >
                            Open <ArrowRight className="w-3 h-3" />
                          </Link>
                          {/* Phase 2L-A58 — per-row archive/restore action for admins.
                              Renders the same ArchiveActionsMenu used by /admin/bookings.
                              Hidden when there's no parent booking we can address by id. */}
                          {isAdmin && booking?.id && (
                            <ArchiveActionsMenu
                              recordType="booking"
                              recordId={booking.id}
                              recordDisplay={booking.reference || test.booking_reference || "this booking"}
                              isArchived={!!booking?.archived_at}
                              onChanged={load}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 mt-5" data-testid="lab-pagination">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            data-testid="lab-page-prev"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>
          {pageNumbers.map((n, idx) => (
            n === "…" ? (
              <span key={`gap-${idx}`} className="px-2 text-slate-400">…</span>
            ) : (
              <button
                key={n}
                onClick={() => setPage(n)}
                data-testid={`lab-page-${n}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  page === n ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                {n}
              </button>
            )
          ))}
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            data-testid="lab-page-next"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      <CreateBookingModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        currentUser={user}
        onCreated={() => { load(); }}
      />

      {/* Phase 2L-A42 — Lab Queue bulk archive confirmation (Admin only). */}
      <AlertDialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <AlertDialogContent data-testid="lab-bulk-archive-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {selectedCount} booking{selectedCount === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                You are about to archive {selectedCount} selected booking
                {selectedCount === 1 ? "" : "s"}. Archived bookings will be
                removed from the Lab Queue and other operational lists but
                can still be viewed via the archive filter.
              </span>
              {selectedPaidCount > 0 && (
                <span
                  data-testid="lab-bulk-archive-paid-warning"
                  className="block px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs font-semibold"
                >
                  ⚠ {selectedPaidCount} of these {selectedPaidCount === 1 ? "is" : "are"} a paid booking.
                  Payment records, reports, audit history, lab results, and
                  email logs are preserved — archive does not delete any of
                  these. Continue only if you intend to hide
                  {selectedPaidCount === 1 ? " this paid booking" : " these paid bookings"} from
                  active queues.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lab-bulk-archive-cancel" disabled={bulkBusy}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="lab-bulk-archive-confirm-button"
              onClick={runBulkArchive}
              disabled={bulkBusy}
              className="bg-slate-900 hover:bg-slate-800"
            >
              {bulkBusy
                ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Archiving…</>
                : <><Archive className="w-4 h-4 mr-1.5" /> Archive {selectedCount}</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PortalShell>
  );
}
